import React, { useState } from"react";
import { useNavigate } from"react-router-dom";
import axios from"axios";
import API_BASE_URL from"../Config/config";
import {
 Grid,
 Box,
 Typography,
 TextField,
 Button,
 Alert,
 IconButton,
 InputAdornment,
 Dialog,
 DialogTitle,
 DialogContent,
 DialogActions,
 MenuItem,
} from"@mui/material";
import { Visibility, VisibilityOff } from"@mui/icons-material";

const Register = () => {
 const navigate = useNavigate();

 const [form, setForm] = useState({
 firstName:"",
 lastName:"",
 collegeName:"",
 state: "",
 username:"",
 email:"",
 mobile:"",
 password:"",
 confirmPassword:"",
 });

 const indianStates = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", 
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", 
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", 
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", 
  "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh", 
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", 
  "Ladakh", "Lakshadweep", "Puducherry"
 ];

 const [showPassword, setShowPassword] = useState(false);
 const [showConfirmPassword, setShowConfirmPassword] = useState(false);
 const [error, setError] = useState("");
 const [success, setSuccess] = useState("");
 const [openDialog, setOpenDialog] = useState(false);
 const [passwordErrors, setPasswordErrors] = useState([]);
 
 // Password validation rules
 const passwordRules = [
 { label:"At least 8 characters", test: (pwd) => pwd.length >= 8 },
 { label:"At least one letter (a-z, A-Z)", test: (pwd) => /[a-zA-Z]/.test(pwd) },
 { label:"At least one number (0-9)", test: (pwd) => /[0-9]/.test(pwd) },
 { label:"At least one symbol (!@#$%^&*()_+-=[]{}|;:',.<>/?", test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd) },
 ];

 // Frontend password validation
 const validatePassword = (password) => {
 const errors = [];
 if (password.length < 8) {
 errors.push("Password must be at least 8 characters long.");
 }
 if (!/[a-zA-Z]/.test(password)) {
 errors.push("Password must contain at least one letter.");
 }
 if (!/[0-9]/.test(password)) {
 errors.push("Password must contain at least one number.");
 }
 if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
 errors.push("Password must contain at least one symbol (!@#$%^&*()_+-=[]{}|;:',.<>/?).");
 }
 return errors;
 };

 const handleChange = (e) => {
 const { name, value } = e.target;
 setForm((prev) => ({
 ...prev,
 [name]: value,
 }));
 
 // Validate password as user types
 if (name ==="password") {
 const errors = validatePassword(value);
 setPasswordErrors(errors);
 }
 
 // Clear password errors when password is cleared
 if (name ==="password" && !value) {
 setPasswordErrors([]);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 setError("");
 setSuccess("");

 // Frontend password validation
 const passwordValidationErrors = validatePassword(form.password);
 if (passwordValidationErrors.length > 0) {
 setError(passwordValidationErrors[0]);
 setPasswordErrors(passwordValidationErrors);
 return;
 }

 if (form.password !== form.confirmPassword) {
 setError("Passwords do not match.");
 return;
 }

 try {
 await axios.post(`${API_BASE_URL}/user/register`, {
 firstName: form.firstName,
 lastName: form.lastName,
 collegeName: form.collegeName,
 state: form.state,
 email: form.email,
 userId: form.username,
 password: form.password,
 phoneNumber: form.mobile || null,
 });

 setSuccess("Registration successful!");
 setOpenDialog(true);
 } catch (err) {
 const errorMessage = err.response?.data?.error || 
 err.response?.data?.message ||
 err.message ||
"Registration failed. Please try again.";
 setError(errorMessage);
 console.error("Registration error:", err.response?.data || err);
 }
 };

 const handleDialogClose = () => {
 setOpenDialog(false);
 navigate("/login");
 };

 return (
 <div style={{ position:"relative" }}>
  {/* ✅ Success Dialog */}
  <Dialog 
    open={openDialog} 
    onClose={handleDialogClose}
    PaperProps={{
      sx: {
        backgroundColor: "#FFFFFF",
        color: "var(--text-body)",
        borderRadius: "24px",
        border: "1px solid var(--accent-gray)",
        boxShadow: "0 25px 50px rgba(31, 138, 203, 0.2)",
      }
    }}
  >
    <DialogTitle sx={{ color: "var(--primary-blue)", fontWeight: 900, fontSize: "1.5rem" }}>Registration Successful</DialogTitle>
    <DialogContent sx={{ color: "var(--text-body)", fontWeight: 500 }}>
      You’ve successfully registered. Please proceed to login.
    </DialogContent>
    <DialogActions sx={{ p: 3 }}>
      <Button 
        onClick={handleDialogClose} 
        autoFocus 
        variant="contained"
        sx={{
          backgroundColor: "var(--primary-blue)",
          color: "#fff",
          fontWeight: 800,
          px: 4,
          py: 1.5,
          borderRadius: "12px",
          textTransform: "none",
          "&:hover": { backgroundColor: "var(--secondary-blue)" }
        }}
      >
        OK
      </Button>
    </DialogActions>
  </Dialog>

  {/* ✅ Back Button */}
  <div style={{ position: "absolute", top: 24, left: 24, zIndex: 10 }}>
    <button
      onClick={() => navigate(-1)}
      className="px-6 py-3 bg-white text-heading font-bold rounded-xl hover:bg-bgMain transition shadow-lg border border-accentGray/20 flex items-center gap-2"
    >
      ← Back
    </button>
  </div>

  <Grid
    container
    justifyContent="center"
    alignItems="center"
    sx={{
      minHeight: "100vh",
      py: 6,
      backgroundColor: "var(--bg-main)",
    }}
  >
    <Box
      sx={{
        width: "100%",
        maxWidth: "1000px",
        borderRadius: 4,
        boxShadow: "0 20px 50px rgba(31, 138, 203, 0.15)",
        backgroundColor: "#FFFFFF",
        color: "var(--text-body)",
        overflow: "hidden",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        border: "1px solid var(--accent-gray)",
      }}
    >
      {/* Left: Form */}
      <Box sx={{ flex: 1, p: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, color: "var(--text-heading)", mb: 1, letterSpacing: "-0.02em" }}>
          SIGN UP
        </Typography>
        <Typography variant="body1" sx={{ color: "var(--text-muted)", mb: 4, fontWeight: 500 }}>
          Already have an account?{" "}
          <a href="/login" style={{ color: "var(--primary-blue)", fontWeight: 700, textDecoration: "none" }}>
            Sign in
          </a>
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, backgroundColor: "rgba(239, 68, 68, 0.1)", color: "#ef4444", borderRadius: "12px" }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, backgroundColor: "rgba(16, 185, 129, 0.1)", color: "#10b981", borderRadius: "12px" }}>{success}</Alert>}

        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
            <TextField
              label="First Name"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true, sx: { color: "var(--text-muted)", fontWeight: 600 } }}
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "var(--bg-main)", color: "var(--text-heading)", fontWeight: 600, borderRadius: "12px", "& fieldset": { borderColor: "var(--accent-gray)" }, "&:hover fieldset": { borderColor: "var(--primary-blue)" } } }}
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true, sx: { color: "var(--text-muted)", fontWeight: 600 } }}
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "var(--bg-main)", color: "var(--text-heading)", fontWeight: 600, borderRadius: "12px", "& fieldset": { borderColor: "var(--accent-gray)" }, "&:hover fieldset": { borderColor: "var(--primary-blue)" } } }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
            <TextField
              label="College Name"
              name="collegeName"
              value={form.collegeName}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true, sx: { color: "var(--text-muted)", fontWeight: 600 } }}
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "var(--bg-main)", color: "var(--text-heading)", fontWeight: 600, borderRadius: "12px", "& fieldset": { borderColor: "var(--accent-gray)" }, "&:hover fieldset": { borderColor: "var(--primary-blue)" } } }}
            />
            <TextField
              select
              label="State of College"
              name="state"
              value={form.state}
              onChange={handleChange}
              fullWidth
              required
              InputLabelProps={{ shrink: true, sx: { color: "var(--text-muted)", fontWeight: 600 } }}
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "var(--bg-main)", color: "var(--text-heading)", fontWeight: 600, borderRadius: "12px", "& fieldset": { borderColor: "var(--accent-gray)" }, "&:hover fieldset": { borderColor: "var(--primary-blue)" } } }}
            >
              {indianStates.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            label="Username"
            name="username"
            value={form.username}
            onChange={handleChange}
            fullWidth
            InputLabelProps={{ shrink: true, sx: { color: "var(--text-muted)", fontWeight: 600 } }}
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "var(--bg-main)", color: "var(--text-heading)", fontWeight: 600, borderRadius: "12px", "& fieldset": { borderColor: "var(--accent-gray)" }, "&:hover fieldset": { borderColor: "var(--primary-blue)" } } }}
          />

          <TextField
            label="Email"
            name="email"
            value={form.email}
            onChange={handleChange}
            fullWidth
            InputLabelProps={{ shrink: true, sx: { color: "var(--text-muted)", fontWeight: 600 } }}
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "var(--bg-main)", color: "var(--text-heading)", fontWeight: 600, borderRadius: "12px", "& fieldset": { borderColor: "var(--accent-gray)" }, "&:hover fieldset": { borderColor: "var(--primary-blue)" } } }}
          />

          <TextField
            label="Mobile Number"
            name="mobile"
            value={form.mobile}
            onChange={handleChange}
            fullWidth
            InputLabelProps={{ shrink: true, sx: { color: "var(--text-muted)", fontWeight: 600 } }}
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "var(--bg-main)", color: "var(--text-heading)", fontWeight: 600, borderRadius: "12px", "& fieldset": { borderColor: "var(--accent-gray)" }, "&:hover fieldset": { borderColor: "var(--primary-blue)" } } }}
          />

          <Box>
            <TextField
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange}
              fullWidth
              error={passwordErrors.length > 0}
              helperText={
                passwordErrors.length > 0 
                ? passwordErrors[0] 
                : form.password 
                ? "Password meets all requirements ✓" 
                : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{
                        backgroundColor: "var(--bg-soft)",
                        borderRadius: "8px",
                        width: "38px",
                        height: "38px",
                        "& svg": { color: "var(--text-muted)" },
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ shrink: true, sx: { color: "var(--text-muted)", fontWeight: 600 } }}
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "var(--bg-main)", color: "var(--text-heading)", fontWeight: 600, borderRadius: "12px", "& fieldset": { borderColor: "var(--accent-gray)" }, "&:hover fieldset": { borderColor: "var(--primary-blue)" } } }}
            />
            
            {/* Password Requirements List */}
            {form.password && (
              <Box sx={{ mt: 1.5, p: 2, backgroundColor: "var(--bg-main)", borderRadius: "12px", border: "1px solid var(--accent-gray)" }}>
                <Typography variant="caption" sx={{ color: "var(--primary-blue)", fontWeight: 800, mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Password Requirements:
                </Typography>
                {passwordRules.map((rule, idx) => {
                  const isValid = rule.test(form.password);
                  return (
                    <Typography
                      key={idx}
                      variant="caption"
                      sx={{
                        display: "block",
                        color: isValid ? "#10b981" : "var(--text-muted)",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      {isValid ? "✓" : "○"} {rule.label}
                    </Typography>
                  );
                })}
              </Box>
            )}
          </Box>

          <TextField
            label="Confirm Password"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={form.confirmPassword}
            onChange={handleChange}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    sx={{
                      backgroundColor: "var(--bg-soft)",
                      borderRadius: "8px",
                      width: "38px",
                      height: "38px",
                      "& svg": { color: "var(--text-muted)" },
                    }}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ shrink: true, sx: { color: "var(--text-muted)", fontWeight: 600 } }}
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "var(--bg-main)", color: "var(--text-heading)", fontWeight: 600, borderRadius: "12px", "& fieldset": { borderColor: "var(--accent-gray)" }, "&:hover fieldset": { borderColor: "var(--primary-blue)" } } }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              background: "var(--brand-gradient)",
              color: "#fff",
              fontWeight: 800,
              fontSize: "1.1rem",
              py: 2,
              borderRadius: "12px",
              textTransform: "none",
              boxShadow: "var(--shadow-md)",
              "&:hover": {
                background: "var(--brand-gradient)",
                opacity: 0.92,
                transform: "translateY(-1px)",
                boxShadow: "var(--shadow-lg)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Register
          </Button>
        </Box>
      </Box>

      {/* Right: Image */}
      <Box
        sx={{
          flex: 1,
          backgroundColor: "var(--bg-soft)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <img
          src="/login2.jpg"
          alt="Skillgen Register"
          style={{
            width: "100%",
            borderRadius: "32px",
            border: "8px solid #FFFFFF",
            boxShadow: "0 20px 40px rgba(31, 138, 203, 0.15)",
          }}
        />
      </Box>
    </Box>
  </Grid>
 </div>
 );
};

export default Register;



